import React, { useEffect, useState } from 'react';
import { ArrowLeft, Heart, Mail, MessageCircle, Pause, Star, X } from 'lucide-react';
import { GetUserDetailAPI } from '../redux/actions/user-management/GetUserDetailAction';
import { useDispatch, useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { formatDate } from '../helpers/DateFormatter';
import axiosInstance from '../api/AxiosInstance';
import { endpoints, image_url } from '../services/endpoints';
import { showErrorToast, showSuccessToast } from '../helpers/SweetAlert';
import Swal from 'sweetalert2';

const UserDetail = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const { response, loading, error } = useSelector((state) => state.userDetail);
    const [messageModalOpen, setMessageModalOpen] = useState(false);
    const [messageText, setMessageText] = useState('');
    const [messageSending, setMessageSending] = useState(false);
    const [suspending, setSuspending] = useState(false);
    const [featuring, setFeaturing] = useState(false);
    const [postsModalOpen, setPostsModalOpen] = useState(false);

    const userId = location.state?.id || params.id;
    const user = response?.data?.user;
    const isSuspended = user?.status === 'banned';
    const recentPosts = response?.data?.recentPosts || [];
    const allRecentPosts = response?.data?.allRecentPosts || recentPosts;
    const backPath = location.state?.from || '/admin-dashboard/user-management';

    const getImageUrl = (path, fallback = '/assets/images/dummy-user.png') => {
        if (!path) return fallback;
        if (/^https?:\/\//i.test(path)) return path;
        return `${image_url}${path}`;
    };

    const renderPostItem = (post) => (
        <div className='recent_post_item inner_outline_blk' key={post?.postId}>
            <div className='recent_post_cover'>
                {post?.mediaUrl ? (
                    <img src={getImageUrl(post.mediaUrl, '')} alt={post?.title || 'Post'} />
                ) : (
                    <span>{post?.postType || 'Post'}</span>
                )}
            </div>
            <div className='recent_post_info'>
                {post?.title && <h3 className='fs-14 fw-semibold mb-1'>{post.title}</h3>}
                <p className='mb-0'>{post?.content || 'No caption available'}</p>
                <div className='recent_post_action_info'>
                    <span><Heart /> {post?.likes || 0} likes</span>
                    <span><MessageCircle /> {post?.comments || 0} comments</span>
                    <span>{post?.createdAt ? formatDate(post.createdAt) : 'N/A'}</span>
                </div>
            </div>
        </div>
    );

    useEffect(() => {
        if (userId) {
            dispatch(GetUserDetailAPI({ userId: userId }))
        }
    }, [dispatch, userId]);

    const closeMessageModal = () => {
        if (messageSending) return;
        setMessageModalOpen(false);
        setMessageText('');
    };

    const handleSendMessage = async (event) => {
        event.preventDefault();
        const trimmedMessage = messageText.trim();

        if (!trimmedMessage) {
            showErrorToast('Message is required');
            return;
        }

        try {
            setMessageSending(true);
            await axiosInstance.post(endpoints.adminSendMessage, {
                userId,
                message: trimmedMessage,
            });
            showSuccessToast('Message sent successfully');
            closeMessageModal();
        } catch (error) {
            showErrorToast(error?.response?.data?.message || 'Unable to send message');
        } finally {
            setMessageSending(false);
        }
    };

    const handleSuspendAccount = async () => {
        if (suspending) return;

        if (isSuspended) {
            const result = await Swal.fire({
                title: 'Reactivate account?',
                text: `This will reactivate ${user?.username || 'this user'}.`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Reactivate',
                cancelButtonText: 'Cancel',
            });

            if (!result.isConfirmed) return;

            try {
                setSuspending(true);
                await axiosInstance.post(endpoints.updateUserStatus, {
                    user_id: userId,
                    status: 'active',
                });
                showSuccessToast('User has been reactivated successfully');
                dispatch(GetUserDetailAPI({ userId }));
            } catch (error) {
                showErrorToast(error?.response?.data?.message || 'Unable to reactivate account');
            } finally {
                setSuspending(false);
            }
            return;
        }

        const result = await Swal.fire({
            title: 'Suspend account?',
            text: `Select how long ${user?.username || 'this user'} should be suspended.`,
            icon: 'warning',
            input: 'select',
            inputOptions: {
                '1_day': '1 Day',
                '1_week': '1 Week',
                '1_month': '1 Month',
                permanent: 'Permanent',
            },
            inputValue: 'permanent',
            showCancelButton: true,
            confirmButtonText: 'Suspend',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d33',
        });

        if (!result.isConfirmed) return;

        try {
            setSuspending(true);
            await axiosInstance.post(endpoints.updateUserStatus, {
                user_id: userId,
                status: 'banned',
                suspension_duration: result.value || 'permanent',
            });
            showSuccessToast('User has been suspended successfully');
            dispatch(GetUserDetailAPI({ userId }));
        } catch (error) {
            showErrorToast(error?.response?.data?.message || 'Unable to suspend account');
        } finally {
            setSuspending(false);
        }
    };

    const handleFeatureUser = async () => {
        if (featuring) return;
        const shouldFeature = !user?.featuredUser;
        let featureDuration = '1_week';

        if (shouldFeature) {
            const result = await Swal.fire({
                title: 'Feature user',
                text: 'Select how long this user should be featured.',
                icon: 'info',
                input: 'select',
                inputOptions: {
                    '1_day': '1 Day',
                    '1_week': '1 Week',
                    '1_month': '1 Month',
                    '1_year': '1 Year',
                },
                inputValue: featureDuration,
                showCancelButton: true,
                confirmButtonText: 'Feature',
                cancelButtonText: 'Cancel',
            });

            if (!result.isConfirmed) return;
            featureDuration = result.value || '1_week';
        } else {
            const result = await Swal.fire({
                title: 'Remove featured user?',
                text: 'This user will no longer appear as featured.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Remove',
                cancelButtonText: 'Cancel',
            });

            if (!result.isConfirmed) return;
        }

        try {
            setFeaturing(true);
            await axiosInstance.post(endpoints.updateUserFeature, {
                user_id: userId,
                featured_user: shouldFeature,
                feature_duration: featureDuration,
            });
            showSuccessToast(shouldFeature ? 'User featured successfully' : 'User removed from featured successfully');
            dispatch(GetUserDetailAPI({ userId }));
        } catch (error) {
            showErrorToast(error?.response?.data?.message || 'Unable to update user feature');
        } finally {
            setFeaturing(false);
        }
    };


    return (
        <>
            {
                loading
                    ? (
                        <div style={{ height: '70vh' }} className='d-flex align-items-center justify-content-center' >
                            <PulseLoader size={25} color="#49a496" />
                        </div >
                    ) : error ? (
                        <div style={{ height: '70vh' }} className='d-flex align-items-center justify-content-center'>
                            <p className='text-danger'>Something went wrong</p>
                        </div>
                    ) :
                        <>
                            <div className="page_top_section">
                                <div className="page-header">
                                    <div className="page-header-left d-flex align-items-center">
                                        <div className="page-header-title">
                                            <nav class="breadcrumb_nav mb-3">
                                                <ul class="breadcrumb mb-0">
                                                    <li class="breadcrumb-item"><span>User Management</span></li>
                                                    <li class="breadcrumb-item text-capitalize">User Details</li>
                                                </ul>
                                            </nav>
                                            <h1 className="fs-3 text-black fw-semibold  mb-0 text-capitalize">User Details</h1>
                                            <p className="text-clr mb-0">{`Complete profile and activity overview for ${response?.data?.user?.username || 'user'}`}</p>
                                        </div>
                                    </div>
                                    <div className="page-header-right ms-auto">
                                        <div className="page-header-right-items">
                                            <div className="d-flex align-items-center gap-2 page-header-right-items-wrapper">
                                                <div className="side-content-blk">
                                                    <button className="outline-btn" type="button" onClick={() => navigate(backPath)}>
                                                        <ArrowLeft />
                                                        <span className="btntext">Back</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <section className="main-section common_section spacer">
                                <div className="container">
                                    <div className="row gy-4">
                                        <div className="col-12">
                                            <div className="row gx-4 gy-4">
                                                <div className="col-sm-12 col-lg-4 col-xl-4">
                                                    <div className='outline_blk'>
                                                        <div className='inner_outline_blk'>
                                                            <div className='user_profile_info vstack justify-content-center align-items-center'>
                                                                <div className='user_preview_cover'>
                                                                    <img src={getImageUrl(response?.data?.user?.profilePic)} alt="User" />
                                                                </div>
                                                                <h2 className='h5'>{response?.data?.user?.username || 'user'}</h2>
                                                                <p>@{response?.data?.user?.username || 'user'}</p>
                                                                <span className={isSuspended ? 'st-inactive' : 'st-active'}>{response?.data?.user?.status ? response?.data?.user?.status.charAt(0).toUpperCase() + response?.data?.user?.status.slice(1) : 'N/A'}</span>
                                                            </div>
                                                            <div className='user_profile_info'>
                                                                <div className='user_profile_item'>
                                                                    <h3>Email</h3>
                                                                    <p className='text-truncate mb-0'>{response?.data?.user?.email || 'N/A'}</p>
                                                                </div>
                                                                <div className='user_profile_item'>
                                                                    <h3>Phone</h3>
                                                                    <p className='text-truncate mb-0'>{response?.data?.user?.phone || 'N/A'}</p>
                                                                </div>
                                                                <div className='user_profile_item'>
                                                                    <h3>Registration Date</h3>
                                                                    <p className='text-truncate mb-0'>{response?.data?.user?.registrationDate ? formatDate(response?.data?.user?.registrationDate) : 'N/A'}</p>
                                                                </div>
                                                                <div className='user_profile_item'>
                                                                    <h3>Last Login</h3>
                                                                    <p className='text-truncate mb-0'>2 hours ago</p>
                                                                </div>
                                                                <div className='user_profile_item'>
                                                                    <h3>Location</h3>
                                                                    <p className='text-truncate mb-0'>{response?.data?.user?.location || 'N/A'}</p>
                                                                </div>
                                                                <div className='user_profile_item'>
                                                                    <h3>Favorite Sports</h3>
                                                                    <div className='favsports_badges_blk hstack gap-2 flex-wrap'>
                                                                        {response?.data?.user?.favoriteSports?.length > 0 ?
                                                                            response?.data?.user?.favoriteSports?.map((fs, index) => {
                                                                                return (
                                                                                    <span key={index}>{fs}</span>
                                                                                )
                                                                            }) :
                                                                            'N/A'
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className='vstack gap-3 mt-3'>
                                                                <button className="primary-btn w-100" type="button" onClick={() => setMessageModalOpen(true)}>
                                                                    <Mail />
                                                                    <span className="btntext">Send Message</span>
                                                                </button>
                                                                <button className="outline-btn w-100" type="button" onClick={handleSuspendAccount} disabled={suspending}>
                                                                    <Pause />
                                                                    <span className="btntext">{suspending ? 'Updating...' : isSuspended ? 'Reactivate Account' : 'Suspend Account'}</span>
                                                                </button>
                                                                <button className="outline-btn w-100" type="button" onClick={handleFeatureUser} disabled={featuring}>
                                                                    <Star fill={user?.featuredUser ? 'currentColor' : 'none'} />
                                                                    <span className="btntext">{featuring ? 'Updating...' : user?.featuredUser ? 'Remove Featured User' : 'Feature User'}</span>
                                                                </button>
                                                                {/* <button className="outline-btn w-100">
                                                                    <Trash />
                                                                    <span className="btntext">Delete Account</span>
                                                                </button> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-sm-12 col-lg-8 col-xl-8">
                                                    <div className='outline_blk'>
                                                        <div className='mb-4'>
                                                            <h2 className='mb-0 fw-semibold fs-4'>Activity Overview</h2>
                                                        </div>
                                                        <div class="common_stats">
                                                            <div class="common_stats_item">
                                                                <span class="common_stats_value">{response?.data?.activity?.postCount}</span>
                                                                <h2>Posts Created</h2>
                                                            </div>
                                                            <div class="common_stats_item">
                                                                <span class="common_stats_value">{response?.data?.activity?.eventCount}</span>
                                                                <h2>Events Joined</h2>
                                                            </div>
                                                            <div class="common_stats_item">
                                                                <span class="common_stats_value">{response?.data?.activity?.challengeWins}</span>
                                                                <h2>Challenges Won</h2>
                                                            </div>
                                                            <div class="common_stats_item">
                                                                <span class="common_stats_value">{response?.data?.activity?.totalLikes}</span>
                                                                <h2>Total Likes</h2>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='outline_blk'>
                                                        <div className='hstack justify-content-between gap-3 flex-wrap mb-3'>
                                                            <h2 className='mb-0 fw-semibold fs-4'>Recent Posts</h2>
                                                            <button className='border-0 p-0 bg-white fs-14 fw-semibold' type="button" onClick={() => setPostsModalOpen(true)} disabled={allRecentPosts.length === 0}>View All</button>
                                                        </div>
                                                        <div className='recent_posts_blk'>
                                                            {recentPosts.length > 0 ? recentPosts.map(renderPostItem) :
                                                                (
                                                                    <div className='recent_post_item inner_outline_blk text-center'>
                                                                        No data available
                                                                    </div>
                                                                )
                                                            }


                                                        </div>
                                                    </div>
                                                    <div className='outline_blk'>
                                                        <div className='mb-3'>
                                                            <h2 className='mb-0 fw-semibold fs-4'>Reports & Violations</h2>
                                                        </div>
                                                        <div className='report_violation_blk'>
                                                            {response?.data?.reports?.list?.length > 0 ?
                                                                response?.data?.reports?.list?.map((report, index) => {
                                                                    return (
                                                                        <div className='report_violation_item inner_outline_blk'>
                                                                            <div className='report_heading hstack justify-content-between gap-3'>
                                                                                <h3>Inappropriate Content</h3>
                                                                                <span className='badge badge-dark'>Resolved</span>
                                                                            </div>
                                                                            <div className='report_comment'>
                                                                                <p>Reported for posting inappropriate language in community chat.</p>
                                                                            </div>
                                                                            <div className='report_info hstack justify-content-between flex-wrap gap-3 mt-2'>
                                                                                <span>Reported by: @sportsfan123</span>
                                                                                <span>Jan 18, 2025</span>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                })
                                                                :

                                                                (
                                                                    <div className='report_violation_item inner_outline_blk'>
                                                                        No data available
                                                                    </div>
                                                                )

                                                            }
                                                        </div>
                                                    </div>
                                                    <div className='outline_blk'>
                                                        <div className='mb-3'>
                                                            <h2 className='mb-0 fw-semibold fs-4'>Account Actions</h2>
                                                        </div>
                                                        <div className='account_report_blk'>
                                                            {response?.data?.accountActions?.length > 0 ?
                                                                response?.data?.accountActions?.map((action, index) => {
                                                                    return (
                                                                        <div className='account_report_item inner_outline_blk'>
                                                                            <div className='account_report_info hstack justify-content-between flex-wrap gap-3 mt-2'>
                                                                                <div className='account_report_text'>
                                                                                    <h3 className='mb-0'>{action?.title || 'N/A'}</h3>
                                                                                    <span>User registered with email verification</span>
                                                                                </div>
                                                                                <span>{action?.date ? formatDate(action?.date) : ''}</span>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                })
                                                                :
                                                                (
                                                                    (
                                                                        <div className='account_report_item inner_outline_blk'>
                                                                            No data available
                                                                        </div>
                                                                    )
                                                                )
                                                            }

                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </section>
                            {messageModalOpen && (
                                <div className="admin-message-backdrop">
                                    <div className="admin-message-modal">
                                        <button className="admin-message-close" type="button" onClick={closeMessageModal} disabled={messageSending} aria-label="Close">
                                            <X />
                                        </button>
                                        <div className="mb-3">
                                            <h2 className="fs-5 fw-semibold mb-1">Send Message</h2>
                                            <p className="mb-0 text-clr">To @{response?.data?.user?.username || 'user'}</p>
                                        </div>
                                        <form onSubmit={handleSendMessage}>
                                            <div className="form-group mb-3">
                                                <label>Message</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="5"
                                                    value={messageText}
                                                    onChange={(event) => setMessageText(event.target.value)}
                                                    placeholder="Write your message"
                                                    disabled={messageSending}
                                                />
                                            </div>
                                            <div className="hstack justify-content-end gap-2">
                                                <button className="outline-btn" type="button" onClick={closeMessageModal} disabled={messageSending}>
                                                    <span className="btntext">Cancel</span>
                                                </button>
                                                <button className="primary-btn" type="submit" disabled={messageSending}>
                                                    <Mail />
                                                    <span className="btntext">{messageSending ? 'Sending...' : 'Send'}</span>
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}
                            {postsModalOpen && (
                                <div className="admin-message-backdrop">
                                    <div className="admin-message-modal admin-posts-modal">
                                        <button className="admin-message-close" type="button" onClick={() => setPostsModalOpen(false)} aria-label="Close">
                                            <X />
                                        </button>
                                        <div className="mb-3">
                                            <h2 className="fs-5 fw-semibold mb-1">All Recent Posts</h2>
                                            <p className="mb-0 text-clr">@{response?.data?.user?.username || 'user'}</p>
                                        </div>
                                        <div className="admin-posts-list">
                                            {allRecentPosts.length > 0 ? allRecentPosts.map(renderPostItem) : (
                                                <div className='recent_post_item inner_outline_blk text-center'>
                                                    No data available
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
            }
        </>
    )
}

export default UserDetail
